import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import translateService from '../services/translateService';

// Create context
const TranslateContext = createContext(null);

/**
 * TranslateProvider - Wraps the app and provides translation functionality
 * Automatically initializes translation service and handles language detection
 */
export function TranslateProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isReady, setIsReady] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    // Initialize translation service
    const detectedLang = translateService.init();
    setCurrentLanguage(detectedLang);
    setIsRTL(translateService.isRTL(detectedLang));
    setIsReady(true);

    // Subscribe to language changes
    const unsubscribe = translateService.subscribe((newLang) => {
      setCurrentLanguage(newLang);
      setIsRTL(translateService.isRTL(newLang));
      // Force re-render all consumers
      forceUpdate(n => n + 1);
    });

    return unsubscribe;
  }, []);

  const setLanguage = useCallback((langCode) => {
    return translateService.setLanguage(langCode);
  }, []);

  const getLanguageInfo = useCallback((langCode = currentLanguage) => {
    return translateService.getLanguageInfo(langCode);
  }, [currentLanguage]);

  // Translation function
  const t = useCallback((key, params) => {
    return translateService.t(key, params);
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    setLanguage,
    getLanguageInfo,
    isRTL,
    isReady,
    languages: translateService.getEnabledLanguages(),
    translationsEnabled: translateService.translationsEnabled,
    t
  };

  return (
    <TranslateContext.Provider value={value}>
      {children}
    </TranslateContext.Provider>
  );
}

/**
 * useTranslateContext - Hook to access translation context
 */
export function useTranslateContext() {
  const context = useContext(TranslateContext);
  if (!context) {
    throw new Error('useTranslateContext must be used within a TranslateProvider');
  }
  return context;
}

export default TranslateProvider;
